import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { BadgeModule } from 'primeng/badge';
import { TooltipModule } from 'primeng/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faCloudArrowUp,
  faPause,
  faXmark,
  faRotate,
  faCodeBranch,
  faFolder,
  faFile,
  faCheck,
  faExclamationTriangle,
  faSpinner,
  faChevronDown,
  faChevronRight
} from '@fortawesome/pro-duotone-svg-icons';

import { UploadService } from '../../services/upload.service';
import { LocaleService } from '../../services/locale.service';
import { UploadBatch, UploadItem } from '../../models/upload.model';
import { UploadItemComponent } from '../upload-item/upload-item.component';
import { LoadFailedComponent } from '../page-states/load-failed.component';
import { L10nPluralPipe } from '../../pipes/l10n-plural.pipe';
import { pluralKey } from '../../utils/plural.utils';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-upload-queue',
  standalone: true,
  imports: [
    ButtonModule,
    ProgressBarModule,
    BadgeModule,
    TooltipModule,
    FontAwesomeModule,
    UploadItemComponent,
    LoadFailedComponent,
    TranslateModule,
    L10nPluralPipe
  ],
  templateUrl: './upload-queue.component.html',
  styleUrls: ['./upload-queue.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UploadQueueComponent implements OnInit {
  private uploadService = inject(UploadService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly localeService = inject(LocaleService);

  // Icons
  faCloudArrowUp = faCloudArrowUp;
  faPause = faPause;
  faXmark = faXmark;
  faRotate = faRotate;
  faCodeBranch = faCodeBranch;
  faFolder = faFolder;
  faFile = faFile;
  faCheck = faCheck;
  faExclamationTriangle = faExclamationTriangle;
  faSpinner = faSpinner;
  faChevronDown = faChevronDown;
  faChevronRight = faChevronRight;

  // State
  batches = this.uploadService.batches;
  /** The resumable-uploads read failed — the tray's face with Try again, never "no uploads" over a read that never landed. */
  resumableLoadFailed = this.uploadService.resumableLoadFailed;
  /** That read is in the air again — the face stays and its button says it is working. */
  resumableReloading = this.uploadService.resumableReloading;
  activeUploadsCount = this.uploadService.activeUploadsCount;
  alertsCount = this.uploadService.alertsCount;
  totalProgress = this.uploadService.totalProgress;

  // Cached ETA per batch — updated on a 1-second timer to avoid NG0100
  batchTimeRemainingCache = new Map<string, string | null>();

  ngOnInit() {
    // Subscribe to upload events if needed
    this.uploadService.uploadEnded$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();

    // Update ETA cache every second — avoids NG0100 from Date.now() in template
    interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshTimeRemainingCache());
  }

  // Actions
  cancelAll() {
    this.uploadService.cancelAll();
  }

  renameAll() {
    this.uploadService.renameAll();
  }

  newVersionForAll() {
    const alerts = this.uploadService.getUploadsByStatus('alert');
    alerts.forEach(upload => {
      this.uploadService.uploadNewVersion(upload.id);
    });
  }

  canCancelAny(): boolean {
    const uploads = this.uploadService.getUploadsByStatus();
    return uploads.some(upload => this.uploadService.canCancel(upload));
  }

  hasAlerts(): boolean {
    return this.alertsCount() > 0;
  }

  // Batch helpers
  getBatchIcon(batch: UploadBatch): any {
    return this.faFolder;
  }

  getBatchStatus(batch: UploadBatch): string {
    const hasError = batch.uploads.some(u => u.status === 'failed');
    if (hasError) return 'error';
    
    const hasAlert = batch.uploads.some(u => u.status === 'alert');
    if (hasAlert) return 'alert';
    
    const hasProgress = batch.uploads.some(u => u.status === 'progress');
    if (hasProgress) return 'progress';
    
    const allDone = batch.uploads.every(u => u.status === 'done');
    if (allDone) return 'done';
    
    return 'queued';
  }

  getActiveUploadsInBatch(batch: UploadBatch): number {
    return batch.uploads.filter(u => 
      u.status === 'progress' || u.status === 'queued'
    ).length;
  }

  getFailedUploadsInBatch(batch: UploadBatch): number {
    return batch.uploads.filter(u => 
      u.status === 'failed' || u.status === 'alert'
    ).length;
  }

  getDoneUploadsInBatch(batch: UploadBatch): number {
    return batch.uploads.filter(u => u.status === 'done').length;
  }

  getCanceledUploadsInBatch(batch: UploadBatch): number {
    return batch.uploads.filter(u => u.status === 'canceled').length;
  }

  /**
   * The batch headline — every arm a keyed sentence (plural families where a count varies) with the
   * destination as DATA; the batch's KIND decides the sentence, never an English title parsed back apart.
   */
  getBatchTitle(batch: UploadBatch): string {
    const activeCount = this.getActiveUploadsInBatch(batch);
    const canceledCount = this.getCanceledUploadsInBatch(batch);
    const doneCount = this.getDoneUploadsInBatch(batch);
    const failedCount = this.getFailedUploadsInBatch(batch);
    const totalCount = batch.uploads.length;

    // Every upload settled the same way: the count says how.
    if (canceledCount === totalCount) return this.plural('upload.canceledCount', totalCount);
    if (doneCount === totalCount) return this.plural('upload.completedCount', totalCount);
    if (failedCount === totalCount) return this.plural('upload.failedCount', totalCount);

    switch (batch.kind) {
      case 'newVersion':
        return this.translate.instant('upload.newVersionOf', { name: batch.destinationName ?? '' });
      case 'resumable':
        return this.translate.instant('upload.resumableTitle');
      case 'upload': {
        // Every family is a sentence PER destination kind (ruled 2026-09-14): in Czech and Polish the classifier
        // before the name is the kind's own noun in the case "do" governs ("do galerie …", never "do složky" for a
        // gallery), and the failed-name arm names the kind in every language ("a gallery we couldn't name").
        const kind = batch.destinationKind;
        if (activeCount > 0) {
          // The destination's name in its slot, or — when its read failed — the failure said as a sentence
          // of its own (never a stand-in like "Unknown", and never a phrase spliced into the name's slot:
          // in Czech and Polish the slot takes a case a spliced phrase cannot bend to).
          return batch.destinationNameFailed
            ? this.plural(`upload.uploadingToUnnamed.${kind}`, activeCount)
            : this.plural(`upload.uploadingTo.${kind}`, activeCount, { destination: batch.destinationName ?? '' });
        }
        // A settled mix of states (no active uploads): the batch is named by where it went.
        return batch.destinationNameFailed
          ? this.translate.instant(`upload.toUnnamedDestination.${kind}`)
          : this.translate.instant(`upload.toDestination.${kind}`, { destination: batch.destinationName ?? '' });
      }
    }
  }

  /** A plural family resolved for the interface language, with `count` fed beside any other slot. */
  private plural(base: string, count: number, params: Record<string, unknown> = {}): string {
    return this.translate.instant(pluralKey(base, count, this.localeService.activeUILanguage()), { count, ...params });
  }

  /** The face's Try again after a failed resumable-uploads read. */
  retryResumable(): void {
    void this.uploadService.retryResumableUploads();
  }

  /** Read cached ETA — stable between change detection cycles. */
  getBatchTimeRemaining(batch: UploadBatch): string | null {
    return this.batchTimeRemainingCache.get(batch.id) ?? null;
  }

  /** Recalculate ETA for all active batches (called on 1-second timer). */
  private refreshTimeRemainingCache(): void {
    const batches = this.uploadService.getUploads();
    const activeIds = new Set<string>();

    for (const batch of batches) {
      activeIds.add(batch.id);
      this.batchTimeRemainingCache.set(batch.id, this.computeTimeRemaining(batch));
    }

    // Clean up stale entries
    for (const key of this.batchTimeRemainingCache.keys()) {
      if (!activeIds.has(key)) {
        this.batchTimeRemainingCache.delete(key);
      }
    }
  }

  /**
   * ETA calculation with post-first-file baseline.
   *
   * Problem: sequential file uploads have inter-file overhead (~1-2s of GraphQL
   * calls between each file). The first file uploads at full network speed with
   * no preceding overhead, inflating the cumulative speed measurement. This causes
   * the ETA to initially underestimate, then INCREASE as overhead accumulates,
   * creating a "stuck" display (e.g. "About 1 min" for 30% of the upload).
   *
   * Fix: after the first file completes, measure speed only from that baseline.
   * This immediately captures the true effective throughput (transfer + overhead)
   * and produces a steady countdown.
   *
   * Single-file batches use cumulative speed (no inter-file overhead issue).
   */
  private computeTimeRemaining(batch: UploadBatch): string | null {
    const hasActive = batch.uploads.some(u => u.status === 'progress' || u.status === 'queued');
    if (!hasActive) return null;

    const remaining = batch.totalSize - batch.uploadedSize;
    if (remaining <= 0) return null;

    // Multi-file batch with baseline: use post-first-file speed
    if (batch.firstFileCompletedAt != null && batch.firstFileSize != null) {
      const elapsedSinceBaseline = (Date.now() - batch.firstFileCompletedAt) / 1000;
      const bytesAfterBaseline = batch.uploadedSize - batch.firstFileSize;

      if (elapsedSinceBaseline >= 3 && bytesAfterBaseline > 0) {
        const speed = bytesAfterBaseline / elapsedSinceBaseline;
        if (speed <= 0) return null;
        return this.formatTimeRemaining((remaining / speed) * 1000);
      }

      // Baseline set but not enough post-baseline data yet
      return null;
    }

    // Before first file completes: don't show ETA for multi-file batches
    // (would be inaccurate and then jump when baseline kicks in)
    if (batch.uploads.length > 1) return null;

    // Single-file batch: use cumulative speed (no inter-file overhead issue)
    if (!batch.uploadStartedAt || batch.uploadedSize <= 0) return null;
    const elapsedMs = Date.now() - batch.uploadStartedAt;
    if (elapsedMs < 3000) return null;

    const speed = batch.uploadedSize / (elapsedMs / 1000);
    if (speed <= 0) return null;
    return this.formatTimeRemaining((remaining / speed) * 1000);
  }

  private formatTimeRemaining(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);

    // Every band is a keyed sentence — plural families where a count varies, flat keys for the two-number bands.
    if (totalSeconds < 10) return this.translate.instant('upload.eta.fewSeconds');

    // Under 1 minute: seconds rounded up to 5.
    if (totalSeconds < 60) {
      const rounded = Math.ceil(totalSeconds / 5) * 5;
      if (rounded >= 60) return this.plural('upload.eta.minutes', 1); // smooth boundary transition
      return this.plural('upload.eta.seconds', rounded);
    }

    // 1 to 5 minutes: minutes and seconds (rounded to 10) so the countdown is always visibly decreasing.
    if (totalSeconds < 300) {
      const m = Math.floor(totalSeconds / 60);
      const s = Math.round((totalSeconds % 60) / 10) * 10;
      if (s === 60) return this.plural('upload.eta.minutes', m + 1);
      if (s === 0) return this.plural('upload.eta.minutes', m);
      return this.translate.instant('upload.eta.minutesSeconds', { minutes: m, seconds: s });
    }

    // 5+ minutes: minutes.
    const minutes = Math.round(totalSeconds / 60);
    if (minutes < 60) return this.plural('upload.eta.minutes', minutes);

    // Hours.
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) return this.plural('upload.eta.hours', hours);
    return this.translate.instant('upload.eta.hoursMinutes', { hours, minutes: remainingMinutes });
  }

  // Track by functions for performance
  trackByBatchId(index: number, batch: UploadBatch): string {
    return batch.id;
  }

  trackByUploadId(index: number, upload: UploadItem): string {
    return upload.id;
  }
}